/**
 * LandingPage — the walk toward the front door of the Residence.
 *
 * Warm ivory canvas, hairline architecture, Fraunces headlines and
 * Inter body. Same design tokens as the foyer and Home OS.
 * The Morning Brief specimen is the hero demonstration — not screenshots,
 * not promotional graphics — the real editorial in miniature.
 */
import { Link } from "react-router-dom";
import {
  EditorialSurface, EditorialColumn, Rule, Colophon, useLightPhase,
} from "./homeos/components/os/editorial";
import AuthMorningBriefPreview from "./homeos/components/os/AuthMorningBriefPreview";

const ROOMS_INTRO = [
  { key: 'today',    name: 'Today',      role: 'The Foyer',            note: 'The morning brief. The day\u2019s three things. Everything else in hand.' },
  { key: 'family',   name: 'Family',     role: 'The Family Room',      note: 'Everyone in the household — their calendars, contributions and current chapter.' },
  { key: 'meals',    name: 'Meals',      role: 'The Kitchen',          note: 'Tonight\u2019s dinner, this week\u2019s plan, and what the pantry has to offer.' },
  { key: 'pantry',   name: 'Shopping',   role: 'The Pantry',           note: 'Provisions running low, what to order next, and how the fridge is doing.' },
  { key: 'money',    name: 'Money',      role: 'The Study',            note: 'The household float, the bills that arrive, the quiet arithmetic of home.' },
  { key: 'health',   name: 'Health',     role: 'The Wellness Room',    note: 'Appointments, notes, and gentle attention to how everyone is doing this week.' },
  { key: 'library',  name: 'Documents',  role: 'The Library',          note: 'Every important record — filed once, retrieved forever, private by default.' },
  { key: 'home',     name: 'Home',       role: 'The House',            note: 'Repairs, warranties, safety checks, and the trades who know your address.' },
  { key: 'planning', name: 'Goals',      role: 'The Planning Studio',  note: 'The longer arc of the household — the year, the season, the intention.' },
];

const ROOM_TONE = {
  today:    'var(--ara-ink)',
  family:   'var(--ara-mod-contacts)',
  meals:    'var(--ara-mod-meals)',
  pantry:   '#6E6F55',
  money:    'var(--ara-mod-money)',
  health:   'var(--ara-mod-health)',
  library:  'var(--ara-mod-documents)',
  home:     'var(--ara-mod-home)',
  planning: 'var(--ara-mod-goals)',
};

const NavBar = () => (
  <nav
    className="w-full sticky top-0 z-20"
    style={{ background: 'var(--ara-surface)', borderBottom: '1px solid var(--ara-line-soft)' }}
    data-testid="landing-nav"
  >
    <div className="mx-auto max-w-[1120px] px-6 sm:px-10 lg:px-16 py-5 flex items-center justify-between">
      <Link to="/" className="caption-ara" style={{ color: 'var(--ara-ink)' }} data-testid="nav-brand">
        ARA
      </Link>
      <div className="flex items-center gap-8">
        <Link
          to="/login"
          className="link-ara"
          style={{ fontSize: 13, letterSpacing: '0.06em', color: 'var(--ara-ink-muted)' }}
          data-testid="nav-signin"
        >
          Sign in
        </Link>
        <Link
          to="/register"
          className="link-ara"
          style={{ fontSize: 13, letterSpacing: '0.06em' }}
          data-testid="nav-register"
        >
          Enter the Residence &nbsp;→
        </Link>
      </div>
    </div>
  </nav>
);

export default function LandingPage() {
  const { phase, hourLabel } = useLightPhase();

  return (
    <EditorialSurface testid="landing-page">
      <div data-phase={phase}>
        <NavBar />

        {/* ═══ Hero ═══ */}
        <section
          className="pt-24 sm:pt-32 lg:pt-48 pb-28 sm:pb-32 rise-ara"
          data-testid="hero"
        >
          <EditorialColumn>
            <p className="caption-ara rise-ara" data-testid="hero-eyebrow">
              ARA · Household operating system
            </p>

            <h1
              className="display-ara mt-10 text-[52px] sm:text-[80px] lg:text-[104px] leading-[1.02] tracking-[-0.02em] max-w-[18ch] rise-ara rise-ara-s1"
              data-testid="hero-title"
            >
              Run your home with calm, clarity and confidence.
            </h1>

            <p
              className="mt-14 prose-ara text-[19px] sm:text-[21px] max-w-[54ch] rise-ara rise-ara-s2"
              data-testid="hero-standfirst"
            >
              ARA is the operating system that quietly coordinates your family&rsquo;s life — from meals and money to maintenance, health and memories. Everything the household holds, in one warm and considered place.
            </p>

            <div className="mt-16 flex flex-wrap items-center gap-8 sm:gap-14 rise-ara rise-ara-s3">
              <Link
                to="/register"
                className="link-ara inline-flex items-center gap-2"
                style={{ fontSize: 15, letterSpacing: '0.06em' }}
                data-testid="hero-primary"
              >
                Enter the Residence &nbsp;→
              </Link>
              <Link
                to="/login"
                className="link-ara inline-flex items-center gap-2"
                style={{ fontSize: 13, letterSpacing: '0.06em', color: 'var(--ara-ink-muted)' }}
                data-testid="hero-secondary"
              >
                View the Morning Brief
              </Link>
            </div>
          </EditorialColumn>
        </section>

        {/* ═══ Section Two — Why households become overwhelmed ═══ */}
        <section className="pb-24 sm:pb-32" data-testid="section-overwhelm">
          <EditorialColumn>
            <Rule className="mb-20" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-10">
              <div className="md:col-span-4">
                <p className="caption-ara">The problem</p>
                <h2 className="display-ara mt-6 text-[32px] sm:text-[42px] leading-tight max-w-[14ch]">
                  Why households become overwhelmed.
                </h2>
              </div>
              <div className="md:col-span-8 md:col-start-5">
                <p className="prose-ara text-[17px] sm:text-[18px] max-w-[60ch]">
                  A modern household holds hundreds of small responsibilities at once. The bills, the birthdays, the dentist, the leaking tap, the school forms, the medicines, the shopping list, the appointment that keeps moving. They arrive from a dozen different apps, texts, sticky notes and quiet mental reminders.
                </p>
                <p className="mt-8 prose-ara text-[17px] sm:text-[18px] max-w-[60ch]">
                  Nothing in software today was designed to hold all of it, gently, on behalf of a family. That is the emptiness ARA was built to fill — not as another calendar, budget or task app, but as the calm architecture that quietly runs the house.
                </p>
              </div>
            </div>
          </EditorialColumn>
        </section>

        {/* ═══ Section Three — The rooms of the residence ═══ */}
        <section className="pb-24 sm:pb-32" data-testid="section-rooms">
          <EditorialColumn>
            <Rule className="mb-20" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-10">
              <div className="md:col-span-4">
                <p className="caption-ara">The residence</p>
                <h2 className="display-ara mt-6 text-[32px] sm:text-[42px] leading-tight max-w-[14ch]">
                  Nine rooms. One house.
                </h2>
                <p className="mt-6 prose-ara text-[15px] max-w-[36ch]">
                  Each room quietly owns a part of household life. The design system is the same throughout — so moving between them feels like walking through the same well-considered home.
                </p>
              </div>

              <div className="md:col-span-8 md:col-start-5">
                {ROOMS_INTRO.map((r, i) => (
                  <div
                    key={r.key}
                    className={`grid grid-cols-[auto_1fr] gap-x-6 py-6 ${i === 0 ? 'border-t' : ''} border-b`}
                    style={{ borderColor: 'var(--ara-line-soft)' }}
                    data-testid={`room-${r.key}`}
                  >
                    <span
                      className="dot-ara mt-3"
                      style={{ background: ROOM_TONE[r.key] }}
                      aria-hidden
                    />
                    <div>
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <p
                          className="font-display text-[22px] sm:text-[26px] leading-tight"
                          style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
                        >
                          {r.name}
                        </p>
                        <p className="caption-ara">{r.role}</p>
                      </div>
                      <p className="mt-3 prose-ara text-[15px] max-w-[54ch]">{r.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </EditorialColumn>
        </section>

        {/* ═══ Section Four — Morning Brief specimen ═══ */}
        <section className="pb-24 sm:pb-32" data-testid="section-brief">
          <EditorialColumn>
            <Rule className="mb-20" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-10">
              <div className="md:col-span-4">
                <p className="caption-ara">Sunday, in miniature</p>
                <h2 className="display-ara mt-6 text-[32px] sm:text-[42px] leading-tight max-w-[14ch]">
                  This is what the house says at 07:12.
                </h2>
                <p className="mt-6 prose-ara text-[15px] max-w-[36ch]">
                  The Morning Brief is the front page of the residence. It reports what most needs the household&rsquo;s attention today — in the same voice, whether the day is calm, attentive or urgent.
                </p>
                <p className="mt-6 prose-ara text-[13px]" style={{ color: 'var(--ara-ink-soft)' }}>
                  Composed at {hourLabel}.
                </p>
              </div>
              <div className="md:col-span-8 md:col-start-5">
                <AuthMorningBriefPreview testid="landing-brief-specimen" />
              </div>
            </div>
          </EditorialColumn>
        </section>

        {/* ═══ Section Five — Why ARA is different ═══ */}
        <section className="pb-24 sm:pb-32" data-testid="section-different">
          <EditorialColumn>
            <Rule className="mb-20" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-10">
              <div className="md:col-span-4">
                <p className="caption-ara">The difference</p>
                <h2 className="display-ara mt-6 text-[32px] sm:text-[42px] leading-tight max-w-[14ch]">
                  ARA is not another app.
                </h2>
              </div>
              <div className="md:col-span-8 md:col-start-5">
                <ul className="space-y-6">
                  {[
                    { k: 'Not budgeting software.',     v: 'The Study cares about your household&rsquo;s calm around money, not about optimising a spreadsheet.' },
                    { k: 'Not a planner.',              v: 'The Family Room holds people, not tasks.' },
                    { k: 'Not a calendar.',             v: 'Appointments live where they belong — in the room that owns them.' },
                    { k: 'Not another productivity app.', v: 'Productivity is the language of offices. Households need something quieter.' },
                  ].map((row, i) => (
                    <li
                      key={i}
                      className={`grid grid-cols-[auto_1fr] gap-x-6 py-4 ${i === 0 ? 'border-t' : ''} border-b`}
                      style={{ borderColor: 'var(--ara-line-soft)' }}
                    >
                      <p className="font-display text-[13px] tabular-nums pt-2" style={{ color: 'var(--ara-ink-soft)' }}>
                        {String(i + 1).padStart(2, '0')}
                      </p>
                      <div>
                        <p className="font-display text-[18px] sm:text-[20px] leading-tight" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                          {row.k}
                        </p>
                        <p className="mt-2 prose-ara text-[15px] max-w-[52ch]" dangerouslySetInnerHTML={{ __html: row.v }} />
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-10 font-display italic text-[19px] max-w-[42ch]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  ARA is the operating system for the household.
                </p>
              </div>
            </div>
          </EditorialColumn>
        </section>

        {/* ═══ Section Six — Privacy ═══ */}
        <section className="pb-24 sm:pb-32" data-testid="section-privacy">
          <EditorialColumn>
            <Rule className="mb-20" />

            <div className="grid grid-cols-1 md:grid-cols-12 gap-x-12 gap-y-10">
              <div className="md:col-span-4">
                <p className="caption-ara">Privacy · the covenant</p>
                <h2 className="display-ara mt-6 text-[32px] sm:text-[42px] leading-tight max-w-[14ch]">
                  The house keeps its own counsel.
                </h2>
              </div>
              <div className="md:col-span-8 md:col-start-5">
                <dl className="space-y-8">
                  {[
                    ['Private by default',           'Every room is closed. Access is granted by the household, never assumed.'],
                    ['Your family owns its data',    'The residence is yours. Export it, move it, close it — always.'],
                    ['No advertising',               'ARA is funded by families, not by attention. We will never sell what the house knows.'],
                    ['No selling household data',    'Not to insurers, not to advertisers, not to anyone. The covenant is absolute.'],
                    ['Designed for trust',           'The interface, the code and the business model are all built to keep this promise.'],
                  ].map(([k, v], i) => (
                    <div key={i} className={`grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-x-8 gap-y-2 pb-6 ${i === 4 ? '' : 'border-b'}`} style={{ borderColor: 'var(--ara-line-soft)' }}>
                      <dt className="caption-ara pt-1">{k}</dt>
                      <dd className="prose-ara text-[15.5px] max-w-[52ch]">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </EditorialColumn>
        </section>

        {/* ═══ Final Section — Welcome home ═══ */}
        <section className="pb-32 pt-24 sm:pt-32" data-testid="section-closing">
          <EditorialColumn>
            <Rule className="mb-24" />

            <div className="max-w-[720px]">
              <p className="caption-ara">A last note</p>
              <h2 className="display-ara mt-10 text-[64px] sm:text-[92px] lg:text-[108px] leading-[1.02] tracking-[-0.02em]">
                Welcome home.
              </h2>
              <p className="mt-14 prose-ara text-[19px] max-w-[46ch]">
                Open a residence in under a minute. Bring your family in at your own pace. Everything the house needs, in the order it needs it.
              </p>
              <div className="mt-16">
                <Link
                  to="/register"
                  className="link-ara inline-flex items-center gap-2"
                  style={{ fontSize: 16, letterSpacing: '0.06em' }}
                  data-testid="closing-cta"
                >
                  Enter the Residence &nbsp;→
                </Link>
              </div>
            </div>
          </EditorialColumn>
        </section>

        <EditorialColumn>
          <Colophon testid="landing-colophon">
            ARA · Household operating system · Set in Fraunces &amp; Inter · Printed on warm ivory
          </Colophon>
        </EditorialColumn>
      </div>
    </EditorialSurface>
  );
}
